<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="d-flex justify-center align-center" >
        <h2>Soins planifiés</h2>
        <v-btn color="third" class="white--text" style="position: absolute; right: 2rem;" @click="createTreatment">Nouveau soin</v-btn>
      </v-col>
      <v-col cols="12">
        <v-skeleton-loader
          v-if="loading"
          class="full-width"
          type="table"
        ></v-skeleton-loader>
        <v-data-table
          v-else
          :headers="headers"
          :items="soinsSessions"
          :items-per-page="-1"
          :height="tableHeight"
          :loading="loading"
          :expanded.sync="expanded"
          :single-expand="false"
          show-expand
          :search="search"
          sort-by="date"
          hide-default-footer
        >
          <!-- Search Expansion -->
          <template #item.data-table-expand="{ item, expand, isExpanded }">
            <v-icon v-if="canExpand(item)" @click="expand(!isExpanded)">
              {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </template>
          <template #item.horseName="{ item }">
            {{ getFirstHorseName(item) }}
            <v-chip v-if="item.horses && item.horses.length > 1" x-small color="third" class="ml-2 white--text">
              +{{ item.horses.length - 1 }}
            </v-chip>
          </template>
          <template #item.date="{ item }">
            {{ formatDate(item.date) }}
          </template>
          <template #item.actions="{ item }">
            <v-icon @click="editTreatment(item)">mdi-pencil</v-icon>
            <v-icon @click="deleteTreatment(item)">mdi-delete</v-icon>
          </template>
          <template #expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-4">
              <div class="d-flex flex-column">
                <div class="subtitle-2 mb-2">Chevaux assignés:</div>
                <v-chip
                  v-for="(horse, index) in item.horses"
                  :key="horse.uuid || index"
                  class="ma-1 white--text"
                  color="third"
                  outlined
                >
                  {{ getHorseName(horse.uuid) }}
                </v-chip>
              </div>
            </td>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <SessionCreate />
    <SessionDetailPopIn />
    <ConfirmationModal title="Supprimer la session" :activator.sync="confirmationModal" :close-modal="closeConfirmationModal" :action="deleteSession" :loading="loading">
      <template #body>
        <h3 class="black--text">
          Êtes-vous sûr de vouloir supprimer la session?
          Une fois supprimée, cette action ne peut pas être annulée et toutes les ressources associées seront également supprimées des sessions
        </h3>
      </template>
    </ConfirmationModal>
  </v-container>
</template>

<script>
import SessionCreate from '@/components/sessions/SessionCreate.vue';
import SessionDetailPopIn from '@/components/sessions/SessionDetailPopIn.vue';
import ConfirmationModal from '@/components/common/ConfirmationModal.vue';

import { ref, onBeforeMount, computed } from '@vue/composition-api';
import { openSearchCustomSessions } from '@/helpers/amplify';
import { format, getUnixTime, endOfDay, addYears, isAfter } from 'date-fns';
import store from '@/store';
import { EventBus, sendNotification } from '@/helpers/utils';
import { handleDeleteSession } from '@/helpers/sessions';

const localReferences = require('@/configs/references.json');

export default {
  name: 'TreatmentsListing',
  components: { SessionCreate, SessionDetailPopIn, ConfirmationModal },
  setup () {
    const headers = ref([
      { text: 'Nom du Cheval', value: 'horseName', align: 'center', sortable: true },
      { text: 'Date', value: 'date', align: 'center', sortable: true },
      { text: 'Heure', value: 'hour', align: 'center', sortable: true },
      { text: 'Type de soin', value: 'treatmentType', align: 'center', sortable: true },
      { text: '', value: 'actions', align: 'center', sortable: false }
    ]);

    const selectedDate = computed(() => store.getters.selectedDate);
    const tableHeight = computed(() => window.innerHeight * 0.7);
    const items = ref([]);
    const loading = ref(false);
    const expanded = ref([]);
    const search = ref(null);
    const getHorsedetail = uuid => store.getters.horse(uuid);
    const confirmationModal = ref(false);
    const sessionToDelete = ref(null);

    const getHorseName = uuid => getHorsedetail(uuid)?.horse_name || 'Informations manquantes sur le cheval';

    const buildTreatmentsQuery = () => {
      const now = new Date();
      const query = {
        bool: { 
          must: [
            { match_phrase: { workspace: store.getters.amplifyWorkspace } },
            { match_phrase: { type: 'SOINS' } },
            { range: { end_time: { gte: getUnixTime(now) } } },
            { range: { end_time: { lte: getUnixTime(endOfDay(addYears(now, 1))) } } }
          ]
        }
      };
      return JSON.stringify(query);
    };

    const computeTreatment = (session) => {
      return {
        id: session.id,
        date: session.start_time,
        hour: format(new Date(session.start_time * 1000), 'HH:mm'),
        treatmentType: localReferences.horse_treatment_type[session.treatment_type] || localReferences.legacy_horse_treatment_type[session.treatment_type] || '--',
        horses: session.horses || [],
      };
    };

    const formatDate = (date) => {
      return format(new Date(date * 1000), 'yyyy/MM/dd');
    };

    const fetchTreatments = async () => {
      const query = buildTreatmentsQuery();
      try {
        items.value = await openSearchCustomSessions(query);
      } catch (error) {
        sendNotification('error', 'Erreur lors de la récupération des soins');
        items.value = [];
      }
    };

    const soinsSessions = computed(() => {
      return (items.value || []).map(session => computeTreatment(session));
    });

    const canExpand = (item) => {
      return item.horses && item.horses.length > 1;
    };

    const getFirstHorseName = (item) => {
      if (!item.horses || !item.horses.length) {
        return '-';
      }
      return getHorseName(item.horses[0].uuid);
    };


    const createTreatment = () => {
      const startTime = new Date(`${selectedDate.value}T${format(new Date(), 'HH:mm')}`);
      const minutes = Math.round(startTime.getMinutes() / 15) * 15;
      startTime.setMinutes(minutes);
      EventBus.$emit('showSessionCreate', { time: format(startTime, 'HH:mm'), typeSession: {type: 'SOINS', mandatory: true}, selectSessionDate: true, title: 'Création de soin' });
    };

    const editTreatment = (item) => {
      const session = items.value.find(s => s.id === item.id);
      if(session) {
        EventBus.$emit('showEditSession', { session: session }, { selectSessionDate: true });
      }
    };
    
    const deleteTreatment = (item) => {
      sessionToDelete.value = item;
      confirmationModal.value = true;
    };

    const closeConfirmationModal = () => {
      confirmationModal.value = false;
      sessionToDelete.value = null;
    };

    const deleteSession = async () => {
      loading.value = true;
      const response = await handleDeleteSession('SINGLE', sessionToDelete.value);
      sendNotification(response.success ? 'success' : 'error', response.message);

      if (response.success) {
        const index = items.value.findIndex(item => item.id === sessionToDelete.value.id);
        if(index !== -1) {
          items.value.splice(index, 1);
        }
      }
      loading.value = false;
      closeConfirmationModal();
    };

    EventBus.$on('createdSessions', (sessions) => {
      (sessions || []).forEach(session => {
        items.value.push(session);
      })
    });

    EventBus.$on('editedSessions', (sessions) => {
      (sessions || []).forEach(session => {
        const index = items.value.findIndex(item => item.id === session.id);
        if(index !== -1) {
          items.value.splice(index, 1, session);
        }
      });
    });

    EventBus.$on('deletedSessions', (sessions) => {
      (sessions || []).forEach(session => {
        const index = items.value.findIndex(item => item.id === session.id);
        if(index !== -1) {
          items.value.splice(index, 1);
        }
      });
    });

    onBeforeMount(async () => {
      loading.value = true;
      
      if (!store.state.isLazyLoaded.horses) {
        await store.dispatch('getHorses');
      }

      await fetchTreatments();

      loading.value = false;
    });

    return {
      headers,
      loading,
      expanded,
      canExpand,
      getFirstHorseName,
      createTreatment,
      editTreatment,
      deleteTreatment,
      tableHeight,
      getHorseName,
      soinsSessions,
      search,
      confirmationModal,
      closeConfirmationModal,
      deleteSession,
      formatDate
    };
  }
};
</script>